import {
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    OpenInNew as ExternalLinkIcon,
} from '@mui/icons-material';
import {
    Collapse,
    Divider,
    DividerProps,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    SxProps,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import { useLoaderData, useLocation, useMatch, useMatches } from 'react-router-dom';

import MenuNewBadge from '@/components/MenuNewBadge';
import ListItemLink from '@/layouts/ManagerLayout/components/ListItemLink';

interface ItemObject {
    label: string;
    icon?: ReactElement;
    to?: string;
    href?: string;
    target?: string;
    action?: () => void;
    sx?: SxProps;
    selected?: boolean;
    returnStatus?: Function;
    isExistMatch?: boolean;
}

interface Props extends ItemObject {
    children?: ItemObject[] | ReactElement;
    isShowDivider?: boolean;
    DividerProps?: DividerProps;
    isActive?: boolean;
}

const MenuItem = ({
    isShowDivider,
    returnStatus = () => {},
    isExistMatch = true,
    ...props
}: ItemObject & { isShowDivider?: boolean }) => {
    const [matchedLink, setMatchedLink] = useState(false);

    const { label, icon, to, target, href, action } = props;
    const { isInquiryNew = false, isReturnIssueAction = false, isUnreadChat } = useLoaderData() as any;

    const location = useLocation();
    const toPath = to || href || '';
    const matched = isExistMatch ? useMatch(toPath) : checkMatched(toPath);

    function checkMatched(path?: string): boolean {
        try {
            if (!path) return false;
            const matchedPath = useMatches().filter((o) => o.pathname.indexOf(toPath) >= 0);

            return matchedPath.length > 0;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    useEffect(() => {
        returnStatus(!!matched, toPath);
        setMatchedLink(!!matched);
    }, [location]);

    return (
        <ListItemLink
            to={to}
            href={href}
            target={target}
            onClick={!!action ? action : undefined}
            {...props}
            selected={matchedLink}
        >
            {!!icon ? <ListItemIcon>{icon}</ListItemIcon> : ''}
            <ListItemText>{label}</ListItemText>
            {label === '플로잉 업데이트' && <MenuNewBadge />}
            {label === '채팅' && !!isUnreadChat && <MenuNewBadge />}
            {!!target && target !== 'self' ? <ExternalLinkIcon color={'disabled'} fontSize={'small'} /> : ''}
        </ListItemLink>
    );
};

const ExpandableMenuItem = (props: Props) => {
    const location = useLocation();

    const isCollapsed = props?.isActive !== false;
    const { label, icon, to, target, href, action, children, isShowDivider = false } = props;
    const { isInquiryNew = false, isReturnIssueAction = false } = useLoaderData() as any;

    const [isOpen, setIsOpen] = useState(isCollapsed);
    const [isActive, setIsActive] = useState(false);

    const activeItemSx = {
        '&.Mui-selected': {
            color: 'var(--v-palette-primary-main, red)',
            backgroundColor: 'transparent',

            '& .MuiListItemIcon-root': {
                color: 'var(--v-palette-primary-main, red)',
            },
        },
    };

    let childrenState: boolean[] = [];
    const handleActiveMatches = (status: boolean, toPath: string) => {
        try {
            childrenState.push(status);

            const total = children instanceof Array ? children?.length : 0;

            if (childrenState?.length && childrenState.length === total) {
                const filtered = childrenState.filter((o) => o);
                if (!!filtered.length) {
                    setIsActive(true);
                    setIsOpen(true);
                } else {
                    setIsActive(false);
                    setIsOpen(isCollapsed);
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <>
            {children ? (
                <>
                    <ListItemButton
                        selected={isActive}
                        onClick={() => {
                            setIsOpen(!isOpen);
                        }}
                        sx={activeItemSx}
                    >
                        {!!icon ? <ListItemIcon>{icon}</ListItemIcon> : ''}
                        <ListItemText>{label}</ListItemText>
                        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItemButton>
                    <Collapse in={isOpen}>
                        <List dense>
                            {!!Array.isArray(children) ? (
                                <>
                                    {children.map((item, index) => {
                                        return (
                                            <MenuItem
                                                {...item}
                                                key={index}
                                                sx={{
                                                    pl: 3,
                                                    ...activeItemSx,
                                                }}
                                                returnStatus={handleActiveMatches}
                                            />
                                        );
                                    })}
                                </>
                            ) : (
                                children
                            )}
                        </List>
                    </Collapse>
                </>
            ) : (
                <MenuItem {...props} sx={activeItemSx} />
            )}
            <Divider {...props?.DividerProps} hidden={!isShowDivider} />
        </>
    );
};

export default ExpandableMenuItem;
